/**
 * 수정자 : 박승호
 * 날짜 : 2024.10.14
 * 수정 이유 : InfoTextWithIcon => TextInfoBox 컴포넌트로 변경
 */

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import RadioContainer from '../../../components/button/RadioContainer';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSetAuth } from '../../../utils/useSetAuth';
import SelectJobtypeBar from './SelectJobtypeBar';
import Portal from '../../../components/Portal';
import JobTypeModal from './JobTypeModal';
import VideoSelect from '../../../components/VideoSelect';
import { toast } from 'react-toastify';
import { IUser, userState } from '../../../atoms';
import { useRecoilValue } from 'recoil';
import { apiPost } from '../../../services/_common';
import { getFileNameFromURL } from '../../../utils/formatFilePath';
import { formatDate } from '../../../utils/formatDate';
import { BtnBlue, BtnRed } from '../../../components/Button';
import DetailsLog from './DetailsLog';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import TextInfoBox from '../../../components/TextInfoBox';
import { useOutletContext } from 'react-router-dom';
import BackButton from '../../../components/BackButton';
import { VIDEO_TYPE_GENERAL, VIDEO_TYPE_JOB } from '../../../_constants';

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  padding: 0.5rem;
  > div {
    gap: 0.5rem;
  }
  justify-content: flex-end;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

const Root = styled.div`
  max-width: none !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  justify-content: flex-start;
  .header {
    padding: 0.75rem;
    height: 3rem;
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    gap: 0.5rem;
    justify-content: space-between;
    .title {
      flex-shrink: 0;
      width: fit-content;
      font-weight: 600;
      margin-right: 0.5rem;
    }
  }
  .video-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    flex-grow: 1;
    overflow: auto;
  }
  .detailInfo-group {
    border-top: 1px solid var(--outline);
    padding: 1.5rem 0;
    margin-top: 0.5rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem 4rem;

    @media (max-width: 767px) {
      grid-template-columns: repeat(1, 1fr);
    }
  }
  .seperator {
    margin: 0.5rem 0;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    div {
      height: 1px;
      flex-grow: 1;
      background-color: rgba(0, 0, 0, 0.1);
    }
    span {
      border-bottom: 1px solid var(--outline);
    }
  }

  .inputForm {
    border-top: 1px solid var(--outline);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem;
    width: 100%;
    height: 3.5rem;
    background-color: transparent;
    > div {
      justify-content: space-between;
      height: inherit;
    }
    button {
      height: 2.5rem;
      font-size: 0.75rem;
      flex-shrink: 0;
      padding: 0 0.75rem;
      border-radius: 0.25rem;
    }
    label {
      width: 10rem;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
    }
    .inputForm-row.labelOutInput {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
    }
  }
  .scrollBox {
    display: flex;
    flex-wrap: wrap;
    overflow: auto;
    height: 100%;
    flex-grow: 1;
  }
`;

const INIT_VIDEO = {
  ko: { isEnrolled: false, path: null, fileName: null, file: null }, // 한국어
  en: { isEnrolled: false, path: null, fileName: null, file: null }, // 영어
  vi: { isEnrolled: false, path: null, fileName: null, file: null }, // 베트남어
  zh: { isEnrolled: false, path: null, fileName: null, file: null }, // 중국어
  th: { isEnrolled: false, path: null, fileName: null, file: null }, // 태국어
  my: { isEnrolled: false, path: null, fileName: null, file: null }, // 미얀마어
  km: { isEnrolled: false, path: null, fileName: null, file: null }, // 캄보디아어
  ru: { isEnrolled: false, path: null, fileName: null, file: null }, // 러시아어
  id: { isEnrolled: false, path: null, fileName: null, file: null }, // 인도네시아어
  ms: { isEnrolled: false, path: null, fileName: null, file: null }, // 말레이시아어
  kk: { isEnrolled: false, path: null, fileName: null, file: null }, // 카자흐스탄어
  mn: { isEnrolled: false, path: null, fileName: null, file: null }, // 몽골어
};

const videos = [
  { caption: '한국어', vType: 'ko' },
  { caption: '영어', vType: 'en' },
  { caption: '베트남어', vType: 'vi' },
  { caption: '중국어', vType: 'zh' },
  { caption: '태국어', vType: 'th' },
  { caption: '미얀마어', vType: 'my' },
  { caption: '캄보디아어', vType: 'km' },
  { caption: '러시아어', vType: 'ru' },
  { caption: '인도네시아어', vType: 'id' },
  { caption: '말레이시아어', vType: 'ms' },
  { caption: '카자흐스탄어', vType: 'kk' },
  { caption: '몽골어', vType: 'mn' },
];

interface Props {
  data: any;
  sType: '1' | '2' | '3';
  selectedIndex: number | null;
  refetch: () => void;
  uploading: boolean;
  setUploading: Dispatch<SetStateAction<boolean>>;
  setSelectedIndex: Dispatch<SetStateAction<number | null>>;
  setViewTable: Dispatch<SetStateAction<boolean>>;
  excludeVideoLanguages?: string[];
}

const VideoDetails = ({ data, sType, selectedIndex, refetch, uploading, setUploading, setSelectedIndex, setViewTable, excludeVideoLanguages }: Props) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue<IUser>(userState);

  const [useYn, setUseYn] = useState<'N' | 'Y'>('N');
  const [jobtypeIdx, setJobtypeIdx] = useState<string>('');

  const INIT_ROW_STATE = { sTitle: '', sSeq: null, video: INIT_VIDEO, delYn: 'N' };
  const [rowState, setRowState] = useState<any>(INIT_ROW_STATE);

  const [openModal, setOpenModal] = useState<{ status: boolean; type: string }>({
    status: false,
    type: 'confirm',
  });
  const size = useOutletContext<any>();

  // 목록에서 선택한 행을 rowState에 적용
  useEffect(() => {
    onClickInit();
  }, [selectedIndex, data]);

  // 상세화면 초기화 함수
  const onClickInit = async () => {
    if (selectedIndex !== null && selectedIndex !== undefined && data[selectedIndex]) {
      const {
        sTitle,
        sSeq,
        delYn,
        wDate,
        writer,
        eDate,
        editor,
        sMovK,
        sMovE,
        sMovV,
        sMovC,
        sMovT,
        sMovM,
        sMovG,
        sMovR,
        sMovI,
        sMovY,
        sMovZ,
        sMovL,
        sMovKUpload,
        sMovEUpload,
        sMovCUpload,
        sMovVUpload,
        sMovTUpload,
        sMovMUpload,
        sMovGUpload,
        sMovRUpload,
        sMovIUpload,
        sMovYUpload,
        sMovZUpload,
        sMovLUpload,
      } = data[selectedIndex];
      if (sType === VIDEO_TYPE_JOB) setJobtypeIdx(data[selectedIndex].jobtypeIdx);
      setUseYn(data[selectedIndex].useYn);
      setRowState({
        sSeq,
        sTitle,
        video: {
          ko: { isEnrolled: !!sMovK, path: sMovK, fileName: getFileNameFromURL(sMovK), file: null, sMovUpload: sMovKUpload }, // 한국어
          en: { isEnrolled: !!sMovE, path: sMovE, fileName: getFileNameFromURL(sMovE), file: null, sMovUpload: sMovEUpload }, // 영어
          vi: { isEnrolled: !!sMovV, path: sMovV, fileName: getFileNameFromURL(sMovV), file: null, sMovUpload: sMovVUpload }, // 베트남어
          zh: { isEnrolled: !!sMovC, path: sMovC, fileName: getFileNameFromURL(sMovC), file: null, sMovUpload: sMovCUpload }, // 중국어
          th: { isEnrolled: !!sMovT, path: sMovT, fileName: getFileNameFromURL(sMovT), file: null, sMovUpload: sMovTUpload }, // 태국어
          my: { isEnrolled: !!sMovM, path: sMovM, fileName: getFileNameFromURL(sMovM), file: null, sMovUpload: sMovMUpload }, // 미얀마어
          km: { isEnrolled: !!sMovG, path: sMovG, fileName: getFileNameFromURL(sMovG), file: null, sMovUpload: sMovGUpload }, // 캄보디아어
          ru: { isEnrolled: !!sMovR, path: sMovR, fileName: getFileNameFromURL(sMovR), file: null, sMovUpload: sMovRUpload }, // 러시아어
          id: { isEnrolled: !!sMovI, path: sMovI, fileName: getFileNameFromURL(sMovI), file: null, sMovUpload: sMovIUpload }, // 인도네시아어
          ms: { isEnrolled: !!sMovY, path: sMovY, fileName: getFileNameFromURL(sMovY), file: null, sMovUpload: sMovYUpload }, // 말레이시아어
          kk: { isEnrolled: !!sMovZ, path: sMovZ, fileName: getFileNameFromURL(sMovZ), file: null, sMovUpload: sMovZUpload }, // 카자흐스탄어
          mn: { isEnrolled: !!sMovL, path: sMovL, fileName: getFileNameFromURL(sMovL), file: null, sMovUpload: sMovLUpload }, // 몽골어
        },
        delYn,
        wDate: formatDate(wDate),
        writer,
        eDate: formatDate(eDate),
        editor,
      });
    } else {
      setUseYn('N');
      setJobtypeIdx('');
      setRowState(INIT_ROW_STATE);
    }
  };

  // 안전교육 제목 onChange 함수
  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length > 50) return;
    setRowState((prev: any) => ({ ...prev, sTitle: value }));
  };

  const onClickJobtypeSelectModal = () => {
    setOpenModal({ status: true, type: 'jobtype' });
  };

  const onClickDelete = () => {
    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: () => onClickSave(true) }));
  };

  const videofilePostAPI = async (req: any, isDelete: boolean) => {
    let path = '';
    if (sType === VIDEO_TYPE_GENERAL || sType === VIDEO_TYPE_JOB) {
      path = '/tablet/videofile';
    } else {
      path = '/visit/videofile';
      delete req.jobtypeIdx;
      delete req.sType;
      delete req.writer;
      delete req.editor;
    }
    console.log(req, '@@');
    const res = await apiPost({ path, contentType: 'multipart/form-data', req });
    console.log(res, '@22');
    try {
      if (res.data.statusCode === 200 && res.data.data) {
        const { sSeq: resSeq, writer: resWriter, editor: resEditor, wDate: resWdate, eDate: resEdate } = res.data.data;
        if (!isDelete) {
          // setIsNewAdd(false);
          setRowState((prev: any) => ({ ...prev, sSeq: resSeq, writer: resWriter, editor: resEditor, wDate: resWdate, eDate: resEdate }));
          toast.success(t(res.data.message));
        } else {
          // setIsNewAdd(true);
          setRowState(INIT_ROW_STATE);
          toast.success(t('삭제 성공'));
        }
        setViewTable(true);
      }
      if (res.status === 412) {
        // contents too large
        toast.warning(t('한 번에 100MB 이하로 업로드 가능합니다'));
      }
      if (res.status === 400) {
        toast.warning(t('잘못된 요청입니다'));
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    } finally {
      setUploading(false);
      refetch();
    }
  };

  // 기존에 사용중인 안전교육이 있는 지 확인
  const checkArrayYnExist = (sSeq: number) => {
    const findUseY = data.find((el: JobtypeVideoRow) => el.useYn === 'Y' && el.sSeq !== sSeq);
    if (findUseY) return true;
    return false;
  };

  const onClickSave = (isDelete: boolean) => {
    const { hCd, sCd, userId } = userInfo;
    const { sSeq, sTitle, delYn, writer, video } = rowState;
    // setIsSaveClicked(true);
    if (sTitle?.trim() === '') return toast.warning(t('필수입력값을 모두 입력하세요'));
    // if (useYn[COMCD_USE_YN] === 'Y' && checkArrayYnExist(sSeq)) return toast.warning(t('이미 사용중인 안전교육이 있습니다.'));
    if (sType === VIDEO_TYPE_JOB && jobtypeIdx === '') return toast.warning(t('직종을 선택하세요'));
    if (!video.ko.file && !video.ko.path && !isDelete) return toast.warning(t('한국어 비디오를 등록하세요'));
    // 비디오 파일 총 합산 100MB 이하인지 체크
    const videoSize =
      (video.ko.file?.size || 0) +
      (video.en.file?.size || 0) +
      (video.vi.file?.size || 0) +
      (video.zh.file?.size || 0) +
      (video.th.file?.size || 0) +
      (video.my.file?.size || 0) +
      (video.km.file?.size || 0) +
      (video.ru.file?.size || 0) +
      (video.id.file?.size || 0) +
      (video.ms.file?.size || 0) +
      (video.kk.file?.size || 0) +
      (video.mn.file?.size || 0);
    if (videoSize > 100 * 1024 * 1024) return toast.warning(t('한 번에 100MB 이하로 업로드 가능합니다'));
    setUploading(true);

    const req = {
      hCd,
      sCd,
      sSeq,
      sType,
      jobtypeIdx,
      sTitle: sTitle?.trim(),
      useYn,
      delYn: isDelete ? 'Y' : delYn,
      writer: writer === undefined ? userInfo.userId : writer,
      editor: userId,
      sMovK: video.ko.file ? video.ko.file : video.ko.path,
      sMovE: video.en.file ? video.en.file : video.en.path,
      sMovC: video.zh.file ? video.zh.file : video.zh.path,
      sMovV: video.vi.file ? video.vi.file : video.vi.path,
      sMovT: video.th.file ? video.th.file : video.th.path,
      sMovM: video.my.file ? video.my.file : video.my.path,
      sMovG: video.km.file ? video.km.file : video.km.path,
      sMovR: video.ru.file ? video.ru.file : video.ru.path,
      sMovI: video.id.file ? video.id.file : video.id.path,
      sMovY: video.ms.file ? video.ms.file : video.ms.path,
      sMovZ: video.kk.file ? video.kk.file : video.kk.path,
      sMovL: video.mn.file ? video.mn.file : video.mn.path,
      sMovKUpload: video.ko.sMovUpload,
      sMovEUpload: video.en.sMovUpload,
      sMovCUpload: video.zh.sMovUpload,
      sMovVUpload: video.vi.sMovUpload,
      sMovTUpload: video.th.sMovUpload,
      sMovMUpload: video.my.sMovUpload,
      sMovGUpload: video.km.sMovUpload,
      sMovRUpload: video.ru.sMovUpload,
      sMovIUpload: video.id.sMovUpload,
      sMovYUpload: video.ms.sMovUpload,
      sMovZUpload: video.kk.sMovUpload,
      sMovLUpload: video.mn.sMovUpload,
    };
    return videofilePostAPI(req, isDelete);
  };

  const onClickDeleteVideo = (vTypeParam: string) => {
    setRowState((prev: any) => ({ ...prev, video: { ...prev.video, [vTypeParam]: { isEnrolled: false, fileName: null, file: null, path: null } } }));
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>, vTypeParam: string) => {
    // event.preventDefault();
    const fileList = event.target.files;

    if (fileList) {
      const file = fileList[0];
      const fileSize = file.size;
      // 80MB in Bytes (80 * 1024 * 1024)
      if (fileSize > 83886080) {
        toast.warning(`${t('파일크기는 80MB를 초과할 수 없습니다')} (${Math.ceil(fileSize / 1024 / 1024)} / 80MB)`);
      } else {
        setRowState((prev: any) => ({ ...prev, video: { ...prev.video, [vTypeParam]: { isEnrolled: true, fileName: file.name, file, path: null } } }));
      }
      event.target.value = '';
    }
  };

  const logs = [
    { name: '등록일자', value: rowState.wDate }, //
    { name: '등록자', value: rowState.writer },
    { name: '수정일자', value: rowState.eDate },
    { name: '수정자', value: rowState.editor },
  ];

  const backToMain = () => {
    setSelectedIndex(null);
    setViewTable(true);
  };

  return (
    <Root>
      {size.innerSize.W <= 1024 && <BackButton func={() => backToMain()} />}
      <div className='header'>
        <span className='title'>{t('안전교육 제목')}</span>
        <input
          type='text' //
          id='sTitle'
          name='sTitle'
          value={rowState.sTitle}
          onChange={(e) => onChangeName(e)}
          disabled={!auth.createAuth && !auth.updateAuth}
          maxLength={50}
        />
        <RadioContainer
          name={t('사용')} //
          onClick={() => setUseYn('Y')}
          isSelected={useYn === 'Y'}
        />
        <RadioContainer
          name={t('미사용')} //
          onClick={() => setUseYn('N')}
          isSelected={useYn === 'N'}
        />
      </div>
      {sType === VIDEO_TYPE_JOB && (
        <SelectJobtypeBar //
          jobtypeIdx={jobtypeIdx}
          setJobtypeIdx={setJobtypeIdx}
          onClickJobtypeSelectModal={onClickJobtypeSelectModal}
        />
      )}
      <TextInfoBox ico='smart_display' description='동영상 확장자는 MP4만 가능합니다' color='orange' />
      <div className='video-container'>
        {videos
          .filter((el) => !excludeVideoLanguages?.includes(el.vType))
          .map((el) => (
            <VideoSelect
              key={el.vType}
              caption={el.caption}
              vType={el.vType}
              file={rowState.video[el.vType].file}
              filePath={rowState.video[el.vType].path}
              fileName={rowState.video[el.vType].fileName}
              isEnrolled={rowState.video[el.vType].isEnrolled}
              handleFileInput={(e: React.ChangeEvent<HTMLInputElement>, type) => handleFileInputChange(e, type)}
              onClickDeleteVideo={(type) => onClickDeleteVideo(type)}
            />
          ))}
        {selectedIndex !== null ? (
          <div className='detailInfo-group'>
            {logs.map((log) => (
              <DetailsLog
                key={log.name} //
                name={t(log.name)} //
                value={log.value}
              />
            ))}
          </div>
        ) : null}
      </div>
      <ButtonsWrapper className='flex-between'>
        <div className='flex-basic'>
          {selectedIndex !== null && auth.deleteAuth && <BtnRed onClick={onClickDelete}>{t('삭제')}</BtnRed>}
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={() => onClickSave(false)}>{uploading ? t('저장중...') : t('저장')}</BtnBlue>}
        </div>
      </ButtonsWrapper>
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'jobtype' && (
          <JobTypeModal
            setSelectedProps={setJobtypeIdx} //
            setOpenModal={setOpenModal}
            selectedProps={jobtypeIdx}
          />
        )}
        {openModal.status && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </Root>
  );
};

export default VideoDetails;
